import { For, Show, type JSX } from 'solid-js'
import type { GoalScope, MetricSelectorScope, PatientAnalyticsScope } from 'mormaii-web-presentation/patient-analytics'

import { SeriesChart, SleepNights } from '../charts'
import { Icon } from '../icons'
import { PeriodPicker } from '../period-picker'
import { ErrorNotice, Loading, Menu, MetricCard, Panel } from '../ui'

import Css from './patient-analytics.module.scss'

/**
 * O painel de um paciente.
 *
 * Desenhado em TRES lugares - o detalhe do colaborador, o do monitoramento e o
 * historico compartilhado - e nao sabe em qual dos tres esta. De onde os numeros vieram (um paciente
 * identificado ou um link publico) e decisao do presenter hospedeiro.
 */

/** Os tres aneis do indice, do maior para o menor. */
const GOALS: Record<GoalScope['kind'], { ring: string; radius: number; icon: string }> = {
    steps: { ring: 'var(--accent)', radius: 108, icon: 'footprints' },
    calories: { ring: 'var(--ok)', radius: 84, icon: 'flame' },
    distance: { ring: 'var(--warn)', radius: 60, icon: 'route' }
}

export function PatientAnalytics(props: { scope: PatientAnalyticsScope }): JSX.Element {
    return (
        <Show when={!props.scope.loading || props.scope.averages.length > 0} fallback={<Loading />}>
            <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
                <div class={Css.panel}>
                    {/* A faixa de controle: o periodo vale para a tela toda. */}
                    <div class={Css.band}>
                        <p class={Css.period}>{props.scope.periodDescription}</p>

                        <Show when={props.scope.period}>
                            {(period) => <PeriodPicker scope={period()} onDark class={Css.selector} />}
                        </Show>
                    </div>

                    {/*
                        AMPLIADO (B-43): so o grafico, na largura toda, com as zonas de FC
                        por idade nos trechos de treino. O normal fica como sempre.
                    */}
                    <Show when={props.scope.expanded}>
                        <Panel
                            title="Evolução no período"
                            actions={
                                <>
                                    <Selector scope={props.scope.metricSelector} />
                                    <ExpandButton scope={props.scope} />
                                </>
                            }
                        >
                            <Show
                                when={
                                    props.scope.nightBars.length > 0 ||
                                    (props.scope.metric === 'SONO' && !props.scope.sleepStages)
                                }
                                fallback={
                                    <SeriesChart
                                        points={props.scope.series}
                                        metric={props.scope.metric}
                                        singleDay={props.scope.period?.from === props.scope.period?.to}
                                        sleepStages={props.scope.sleepStages}
                                        height={520}
                                        bands={props.scope.references}
                                    />
                                }
                            >
                                {/* O SONO num periodo: uma barra por noite, do deitar ao acordar (B-41, B-39). */}
                                <SleepNights
                                    bars={props.scope.nightBars}
                                    summary={props.scope.sleepSummary}
                                    height={520}
                                />
                            </Show>
                            <Show when={props.scope.references.length > 0}>
                                <div class={Css.legend}>
                                    <For each={props.scope.references}>
                                        {(reference) => (
                                            <span class={Css.legendItem}>
                                                <span
                                                    class={Css.legendSwatch}
                                                    style={{ 'background-color': reference.color }}
                                                />
                                                {reference.label}
                                            </span>
                                        )}
                                    </For>
                                </div>
                            </Show>
                            <Show
                                when={
                                    props.scope.metric === 'FREQUENCIA_CARDIACA' && props.scope.references.length === 0
                                }
                            >
                                <p class={Css.note}>
                                    As zonas por idade aparecem nos trechos de treino, quando a data de nascimento está
                                    preenchida.
                                </p>
                            </Show>
                        </Panel>
                    </Show>

                    <Show when={!props.scope.expanded}>
                        <div class={Css.cards}>
                            <For each={props.scope.averages}>
                                {(average) => (
                                    <MetricCard
                                        icon={average.icon}
                                        label={average.label}
                                        value={average.value}
                                        hint={average.subtitle}
                                        spark={average.spark}
                                    />
                                )}
                            </For>
                        </div>

                        <div class={Css.charts}>
                            <Panel
                                class={Css.chart}
                                title="Evolução no período"
                                actions={
                                    <>
                                        <Selector scope={props.scope.metricSelector} />
                                        <ExpandButton scope={props.scope} />
                                    </>
                                }
                            >
                                <Show
                                    when={
                                        props.scope.nightBars.length > 0 ||
                                        (props.scope.metric === 'SONO' && !props.scope.sleepStages)
                                    }
                                    fallback={
                                        <SeriesChart
                                            points={props.scope.series}
                                            metric={props.scope.metric}
                                            singleDay={props.scope.period?.from === props.scope.period?.to}
                                            sleepStages={props.scope.sleepStages}
                                            height={320}
                                        />
                                    }
                                >
                                    {/* O SONO num periodo: uma barra por noite, do deitar ao acordar (B-41, B-39). */}
                                    <SleepNights
                                        bars={props.scope.nightBars}
                                        summary={props.scope.sleepSummary}
                                        height={320}
                                    />
                                </Show>
                            </Panel>

                            <Panel title="Índice de saúde" actions={<span class={Css.note}>Média do período</span>}>
                                <HealthIndex goals={props.scope.goals} />
                            </Panel>
                        </div>
                    </Show>
                </div>
            </Show>
        </Show>
    )
}

/** O botao de ampliar e voltar o grafico (B-43). */
function ExpandButton(props: { scope: PatientAnalyticsScope }): JSX.Element {
    return (
        <button
            type="button"
            class={Css.expand}
            onClick={() => void props.scope.onToggleExpanded()}
            aria-pressed={props.scope.expanded}
            title={props.scope.expanded ? 'Voltar ao tamanho normal' : 'Ampliar o gráfico'}
            aria-label={props.scope.expanded ? 'Voltar ao tamanho normal' : 'Ampliar o gráfico'}
        >
            <Icon name={props.scope.expanded ? 'minimize' : 'maximize'} size={16} />
        </button>
    )
}

/**
 * Os tres aneis concentricos, e a legenda embaixo.
 *
 * Aneis e nao barras porque os tres indicadores sao da MESMA natureza - quanto da
 * meta do dia foi cumprido - e o anel compara os tres de relance. Cor e raio sao
 * decisao daqui; o que a apresentacao diz e de que indicador se trata (`kind`).
 */
function HealthIndex(props: { goals: GoalScope[] }): JSX.Element {
    return (
        <div class={Css.score}>
            <svg viewBox="0 0 280 280" class={Css.donut} aria-hidden="true">
                <For each={props.goals}>
                    {(goal) => {
                        const shape = GOALS[goal.kind]
                        const circumference = 2 * Math.PI * shape.radius

                        return (
                            <>
                                <circle
                                    cx="140"
                                    cy="140"
                                    r={shape.radius}
                                    fill="none"
                                    stroke="var(--line-2)"
                                    stroke-width="18"
                                />
                                <circle
                                    cx="140"
                                    cy="140"
                                    r={shape.radius}
                                    fill="none"
                                    stroke={shape.ring}
                                    stroke-width="18"
                                    stroke-linecap="round"
                                    stroke-dasharray={String(circumference)}
                                    stroke-dashoffset={String(
                                        circumference - (Math.min(100, goal.percentage) / 100) * circumference
                                    )}
                                    transform="rotate(-90 140 140)"
                                />
                            </>
                        )
                    }}
                </For>
            </svg>

            <ul class={Css.targets}>
                <For each={props.goals}>
                    {(goal) => (
                        <li class={Css.target}>
                            <span
                                class={Css.targetIcon}
                                style={{
                                    'background-color': `color-mix(in srgb, ${GOALS[goal.kind].ring} 14%, white)`,
                                    color: GOALS[goal.kind].ring
                                }}
                            >
                                <Icon name={GOALS[goal.kind].icon} size={16} />
                            </span>

                            <div class={Css.targetTexts}>
                                <p class={Css.targetName}>{goal.label}</p>
                                <p class={Css.targetDetail}>
                                    {goal.value} de {goal.goal}
                                </p>
                            </div>

                            <span class={Css.targetValue}>{Math.round(goal.percentage)}%</span>
                        </li>
                    )}
                </For>
            </ul>
        </div>
    )
}

function Selector(props: { scope?: MetricSelectorScope; onDark?: boolean }): JSX.Element {
    return (
        <Show when={props.scope}>
            {(scope) => (
                <Menu
                    summary={scope().summary}
                    onDark={props.onDark}
                    options={scope().options.map((option) => ({
                        label: option.label,
                        current: option.current,
                        onSelect: option.onSelect
                    }))}
                />
            )}
        </Show>
    )
}
